
import { useState, useRef, useEffect } from 'react'
import { MapPin, Search } from 'lucide-react'
import { GameLocation } from '@/types/game'

interface PlaceAutocompleteProps {
  value: GameLocation
  onChange: (location: GameLocation) => void
  placeholder?: string
  required?: boolean
  establishmentsOnly?: boolean // true = apenas estabelecimentos, false = permite endereços
  // When true, also surface United States / Alaska suggestions in addition to
  // Brazil. Used by the user-location picker so reviewers can set their
  // location to the Anchorage demo venues. Places region filtering is
  // country-level, so non-Alaska US results are dropped client-side.
  allowAlaska?: boolean
}

interface PlacePrediction {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}


export function PlaceAutocomplete({ value, onChange, placeholder = "Buscar local...", required = false, establishmentsOnly = false, allowAlaska = false }: PlaceAutocompleteProps) {
  const [query, setQuery] = useState(value.name || '')
  const [predictions, setPredictions] = useState<PlacePrediction[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const isGoogleMapsLoaded = useRef(false)

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        isGoogleMapsLoaded.current = true
        return true
      }
      return false
    }

    // Try to check immediately
    if (checkGoogleMaps()) {
      return
    }

    // If not loaded yet, poll until it is
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [])

  const searchPlaces = async (searchQuery: string) => {
    if (searchQuery.length < 3) {
      setPredictions([])
      return
    }

    if (!isGoogleMapsLoaded.current) {
      console.error('Google Maps not loaded yet')
      return
    }

    setIsLoading(true)

    try {
      const request: google.maps.places.AutocompleteSuggestionRequest = {
        input: searchQuery,
        // Region filtering is country-level (CLDR codes). To surface Alaska we
        // include the whole US and drop non-Alaska US results client-side below.
        includedRegionCodes: allowAlaska ? ['br', 'us'] : ['br'],
        language: 'pt-BR'
      }

      // Bias the results toward a region. A circle is only valid for
      // `locationBias` — `locationRestriction` accepts rectangular bounds only,
      // so passing a circle there throws InvalidValueError.
      if (establishmentsOnly) {
        request.locationBias = {
          radius: 50000, // 50km
          center: { lat: -23.5505, lng: -46.6333 } // São Paulo como padrão
        }
      }

      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      setIsLoading(false)

      if (suggestions && suggestions.length > 0) {
        const convertedPredictions: PlacePrediction[] = suggestions
          .filter(suggestion => suggestion.placePrediction) // Apenas place predictions
          .map((suggestion) => {
            const placePrediction = suggestion.placePrediction!

            return {
              place_id: placePrediction.placeId,
              description: placePrediction.text?.text || placePrediction.text || '',
              structured_formatting: {
                main_text: placePrediction.mainText?.text || placePrediction.mainText || '',
                secondary_text: placePrediction.secondaryText?.text || placePrediction.secondaryText || ''
              }
            }
          })

        // With `allowAlaska` we requested the whole US (region filtering is
        // country-level), so keep Brazilian results plus US results that are in
        // Alaska, and drop the rest of the US.
        const visiblePredictions = allowAlaska
          ? convertedPredictions.filter((p) => {
              const text = `${p.structured_formatting.main_text} ${p.structured_formatting.secondary_text} ${p.description}`
              const isUS = /\b(USA|EUA)\b|United States|Estados Unidos/i.test(text)
              const isAlaska = /\bAK\b|Alas[ck]a/i.test(text)
              return !isUS || isAlaska
            })
          : convertedPredictions

        setPredictions(visiblePredictions)
      } else {
        console.log('No predictions found')
        setPredictions([])
      }
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error)
      setPredictions([])
      setIsLoading(false)
    }
  }

  const getPlaceDetails = async (placeId: string) => {
    if (!isGoogleMapsLoaded.current) {
      console.error('Google Maps not loaded yet')
      return
    }

    try {
      const place = new google.maps.places.Place({
        id: placeId,
        requestedLanguage: 'pt-BR'
      })

      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'location']
      })

      const location: GameLocation = {
        name: place.displayName || '',
        address: place.formattedAddress || '',
        googlePlaceID: place.id || null,
        coordinate: {
          latitude: place.location?.lat() || 0,
          longitude: place.location?.lng() || 0
        }
      }

      onChange(location)
      setQuery(place.displayName || place.formattedAddress || '')
      setIsOpen(false)
      setPredictions([])
    } catch (error) {
      console.error('Error fetching place details:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value
    setQuery(newQuery)
    setIsOpen(true)

    if (newQuery.length >= 3) {
      searchPlaces(newQuery)
    } else {
      setPredictions([])
    }

    // Clear location when typing manually
    if (newQuery !== value.name) {
      onChange({
        name: '',
        address: '',
        googlePlaceID: null,
        coordinate: { latitude: 0, longitude: 0 }
      })
    }
  }

  const handleSelectPrediction = (prediction: PlacePrediction) => {
    getPlaceDetails(prediction.place_id)
  }

  const handleInputFocus = () => {
    setIsOpen(true)
    if (query.length >= 3) {
      searchPlaces(query)
    }
  }

  const handleInputBlur = () => {
    // Delay closing to allow selection
    setTimeout(() => setIsOpen(false), 200)
  }

  return (
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="w-5 h-5 text-brand-secondary" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="w-full pl-10 pr-10 py-3 border border-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <Search className="w-5 h-5 text-text-secondary animate-pulse" />
          </div>
        )}
      </div>

      {/* Predictions Dropdown */}
      {isOpen && predictions.length > 0 && (
        <div className="fixed inset-0 z-[9999]" onClick={() => setIsOpen(false)}>
          <div
            className="absolute bg-background-white border border-border-primary rounded-lg shadow-lg"
            style={{
              top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + window.scrollY + 4 : 0,
              left: inputRef.current ? inputRef.current.getBoundingClientRect().left + window.scrollX : 0,
              width: inputRef.current ? inputRef.current.getBoundingClientRect().width : 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-h-60 overflow-y-auto">
            {predictions.map((prediction) => (
              <button
                key={prediction.place_id}
                type="button"
                onClick={() => handleSelectPrediction(prediction)}
                className="w-full text-left px-4 py-3 hover:bg-background-secondary transition-colors border-b border-gray-200/10 last:border-b-0"
              >
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-brand-secondary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-text-primary truncate">
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div className="text-sm text-text-secondary truncate">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                </div>
              </button>
            ))}
            </div>
          </div>
        </div>
      )}

      {/* No results message */}
      {isOpen && !isLoading && query.length >= 3 && predictions.length === 0 && (
        <div className="fixed inset-0 z-[9999]" onClick={() => setIsOpen(false)}>
          <div
            className="absolute bg-background-white border border-border-primary rounded-lg shadow-lg"
            style={{
              top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + window.scrollY + 4 : 0,
              left: inputRef.current ? inputRef.current.getBoundingClientRect().left + window.scrollX : 0,
              width: inputRef.current ? inputRef.current.getBoundingClientRect().width : 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 text-sm text-text-secondary text-center">
              Nenhum local encontrado
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
